import React, { useState, useEffect } from 'react';
import { storage, firestore } from './firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const EventsInput = () => {
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventImages, setEventImages] = useState([]);
  const [eventDescription, setEventDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [existingImageUrls, setExistingImageUrls] = useState([]);

  useEffect(() => {
    const fetchEvents = async () => {
      const eventsCollection = collection(firestore, 'events');
      const eventsSnapshot = await getDocs(eventsCollection);
      setEvents(eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    fetchEvents();
  }, []);

  const handleAddOrUpdateEvent = async () => {
    if (eventName && eventDate && (eventImages.length > 0 || existingImageUrls.length > 0) && eventDescription) {
      setUploading(true);

      try {
        let imageUrls = existingImageUrls;

        if (eventImages.length > 0) {
          const uploadPromises = eventImages.map((image) => {
            const storageRef = ref(storage, `events/${image.name}`);
            const uploadTask = uploadBytesResumable(storageRef, image);

            return new Promise((resolve, reject) => {
              uploadTask.on(
                'state_changed',
                null,
                (error) => reject(error),
                () => {
                  getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject);
                }
              );
            });
          });

          imageUrls = await Promise.all(uploadPromises);
        }

        if (selectedEventId) {
          const eventDoc = doc(firestore, 'events', selectedEventId);
          await updateDoc(eventDoc, {
            name: eventName,
            date: eventDate,
            imageUrls,
            description: eventDescription,
          });
        } else {
          await addDoc(collection(firestore, 'events'), {
            name: eventName,
            date: eventDate,
            imageUrls,
            description: eventDescription,
          });
        }

        // Reset input fields
        setEventName('');
        setEventDate('');
        setEventImages([]);
        setEventDescription('');
        setSelectedEventId(null);
        setExistingImageUrls([]);
      } catch (error) {
        console.error('Error uploading images or saving event:', error);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleEditEvent = (event) => {
    setSelectedEventId(event.id);
    setEventName(event.name);
    setEventDate(event.date);
    setExistingImageUrls(event.imageUrls);
    setEventDescription(event.description);
    setEventImages([]);
  };

  const handleDeleteEvent = async (eventId, imageUrls) => {
    try {
      const eventDoc = doc(firestore, 'events', eventId);
      await deleteDoc(eventDoc);

      // Delete images from storage
      const deletePromises = imageUrls.map((url) => {
        const imageRef = ref(storage, `events/${url.split('/').pop().split('?')[0]}`);
        return deleteObject(imageRef);
      });
      await Promise.all(deletePromises);

      setEvents(events.filter(event => event.id !== eventId));
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  return (
    <div>
      <h2>{selectedEventId ? 'Update Event' : 'Add a New Event'}</h2>
      <input
        type="text"
        placeholder="Event Name"
        value={eventName}
        onChange={(e) => setEventName(e.target.value)}
      />
      <input
        type="date"
        value={eventDate}
        onChange={(e) => setEventDate(e.target.value)}
      />
      <input
        type="file"
        multiple
        onChange={(e) => setEventImages([...e.target.files])}
      />
      <textarea
        placeholder="Event Description"
        value={eventDescription}
        onChange={(e) => setEventDescription(e.target.value)}
      />
      <button onClick={handleAddOrUpdateEvent} disabled={uploading}>
        {uploading ? 'Uploading...' : selectedEventId ? 'Update Event' : 'Add Event'}
      </button>

      <h3>Existing Events</h3>
      <ul>
        {events.map(event => (
          <li key={event.id}>
            <strong>{event.name}</strong> ({event.date})
            <button onClick={() => handleEditEvent(event)}>Edit</button>
            <button onClick={() => handleDeleteEvent(event.id, event.imageUrls)}>Delete</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default EventsInput;
